import React, { useCallback, useEffect, useRef, useState } from 'react';
import AppLayout from '../../../../component/appLayout';
import {
  Group,
  Header,
  PersonalText,
  ProjectContent,
  Text,
  Text1,
  UserProfile,
  Container,
  ImageUploadText
} from '../../styles';
import Button from '../../../../component/button';
import SVGIcon from '../../../../assets/images/svg/SVGIcon';
import {
  BUTTON_TYPE,
  ICON_BUTTON_SIZE,
  ICON_BUTTON_TYPE,
  ICON_SIZE,
  IMAGE_COMPRESSION,
  invoiceDueOptions
} from '../../../../global/constants';
import { getFirebaseUrlFromFile, handleFileReadImage, isEmpty, isFloatOrNumber } from '../../../../helpers/common';
import { getInvoicingSettingsData, updateInvoicingSettings } from '../../../../services/settingServices';
import { captureException } from '../../../../services/logService';
import ResponsiveHeader from '../../../../component/responsiveHeader';
import { useMobileDevice } from '../../../../global/useMobile';
import RadioButton from '../../../../component/radioButton';
import { rootStore } from '../../../../mobx/rootStore';
import BottomSubNavigation from '../../../../component/bottomNavigation/subNavigation';
import BottomNavigationItem from '../../../../component/bottomNavigation/subNavigation/bottomNavigationItem';
import { observer } from 'mobx-react-lite';
import IconButton from '../../../../component/button/iconButton';
import Input from '../../../../component/input';
import TextArea from '../../../../component/input/textArea';
import {
  Title,
  SectionTitle,
  UploadText,
  SubContent,
  Autotext,
  InvoiceLogo,
  InputSection,
  FormContent,
  InputSide,
  AddTask,
  PlusIcon,
  Features,
  InvoiceDue,
  RadioButtonWrapper,
  Overlay,
  EditIcon
} from './styles';
import Image from '../../../../component/image';

interface ImageState {
  profile: string | ArrayBuffer | null;
}

const Invoicing: React.FC = () => {
  // Refs
  const inputRef = useRef<HTMLInputElement | null>(null);
  // States
  const [image, setImage] = useState<ImageState | null>(null);
  const [loading, setLoading] = useState(false);
  //mobx store
  const { settingStore, workspaceStore } = rootStore || {};
  const { invoicingDetail } = settingStore;
  const { currentWorkspace } = workspaceStore;

  // Other variables
  const isMobile = useMobileDevice();
  const showImage = !isEmpty(image?.profile) || !isEmpty(invoicingDetail?.invoiceLogo);
  const imageSource = !isEmpty(image?.profile)
    ? image?.profile?.toString()
    : !isEmpty(invoicingDetail?.invoiceLogo)
    ? invoicingDetail?.invoiceLogo
    : 'invoicing_empty_profile';

  // Loads invoicing settings data from the server
  const loadData = useCallback(async () => {
    try {
      if (!isEmpty(currentWorkspace?.id)) {
        setLoading(true);
        const result = await getInvoicingSettingsData(currentWorkspace?.id);
        rootStore.settingStore.setInvoicingDetail({
          ...result,
          ...((isEmpty(result?.tax) || !result?.tax || result?.tax?.length === 0) && {
            tax: [{ name: '', value: null }]
          })
        });
      }
    } catch (error) {
      captureException(error);
      setLoading(false);
    } finally {
      setLoading(false);
    }
  }, [currentWorkspace]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Adds a new tax rate item to the list
  const onAddItems = useCallback(async () => {
    let itemClone = JSON.parse(JSON.stringify(invoicingDetail?.tax));
    let Clone = {
      name: '',
      value: null
    };
    const newItems = [...itemClone, Clone];
    rootStore.settingStore.updateInvoicingDetail('tax', newItems);
  }, [invoicingDetail?.tax]);

  // Deletes a tax rate item from the list
  const onDeleteItem = useCallback(
    (index: number) => {
      let items = JSON.parse(JSON.stringify(invoicingDetail?.tax));
      if (items?.length <= 1) {
        items = [
          {
            name: '',
            value: 0
          }
        ];
      } else {
        items.splice(index, 1);
      }
      rootStore.settingStore.updateInvoicingDetail('tax', items);
    },
    [invoicingDetail?.tax]
  );

  // Updates tax rate item input fields
  const onChangeItemsInputField = useCallback(
    (index: number, propsName: string, value: string) => {
      if (propsName === 'value' && !isFloatOrNumber(value)) {
        rootStore.alertNotificationStore.setErrorMessage('Please enter only digits in tax amount!');
        return;
      }
      let items = JSON.parse(JSON.stringify(invoicingDetail?.tax));
      items[index][propsName] = value;
      rootStore.settingStore.updateInvoicingDetail('tax', items);
    },
    [invoicingDetail?.tax]
  );

  // Updates general input fields
  const onChangeInputField = useCallback((propsName: string, value: string | number) => {
    // Convert value to number if it's a string and we're dealing with dueDays
    const processedValue = propsName === 'dueDays' ? Number(value) : value;
    rootStore.settingStore.updateInvoicingDetail(propsName, processedValue);
  }, []);

  // Handles form submission and saves invoicing settings
  const onSubmit = useCallback(
    async (e: React.SyntheticEvent) => {
      try {
        e.preventDefault();
        setLoading(true);
        let profile;
        if (image) {
          profile = await getFirebaseUrlFromFile(image, 'invoice-logo/', 'invoice-logo');
        }
        if (Array.isArray(invoicingDetail?.tax)) {
          for (const item of invoicingDetail.tax) {
            if (!isEmpty(item?.name) && isEmpty(item?.value)) {
              rootStore.alertNotificationStore.setErrorMessage('Tax rate should not be empty!');
              return;
            } else if (isEmpty(item?.name) && !isEmpty(item?.value)) {
              rootStore.alertNotificationStore.setErrorMessage('Tax name should not be empty!');
              return;
            }
          }
        }
        const taxRates = invoicingDetail?.tax
          ?.map((item) => ({
            ...item,
            value: Number(item?.value)
          }))
          ?.filter((item) => !isEmpty(item?.value) && !isEmpty(item.name));
        const payload = {
          name: !isEmpty(invoicingDetail?.name) ? invoicingDetail?.name : undefined,
          address: !isEmpty(invoicingDetail?.address) ? invoicingDetail?.address : undefined,
          tax: !isEmpty(taxRates) ? taxRates : undefined,
          note: !isEmpty(invoicingDetail?.note) ? invoicingDetail?.note : undefined,
          dueDays: !isEmpty(invoicingDetail?.dueDays) ? invoicingDetail?.dueDays : undefined,
          invoiceLogo: !isEmpty(profile)
            ? profile
            : !isEmpty(invoicingDetail?.invoiceLogo)
            ? invoicingDetail?.invoiceLogo
            : undefined
        };
        const result = await updateInvoicingSettings(currentWorkspace?.id, payload);
        if (result) {
          loadData();
        }
      } catch (error) {
        captureException(error);
        setLoading(false);
      } finally {
        setLoading(false);
      }
    },
    [
      image,
      invoicingDetail?.address,
      invoicingDetail?.dueDays,
      invoicingDetail?.invoiceLogo,
      invoicingDetail?.name,
      invoicingDetail?.note,
      invoicingDetail?.tax,
      loadData,
      currentWorkspace
    ]
  );

  // Triggers file upload input click
  const handleUploadClick = () => {
    inputRef.current?.click();
  };

  // Handles file upload and validation
  const handleFileRead = async (event: React.ChangeEvent<HTMLInputElement>) => {
    // Company logos are hard-capped to 256x256 / 50KB before upload.
    const result = await handleFileReadImage(event, {
      maxWidth: IMAGE_COMPRESSION.AVATAR_MAX_WIDTH,
      maxHeight: IMAGE_COMPRESSION.AVATAR_MAX_HEIGHT,
      maxSizeBytes: IMAGE_COMPRESSION.AVATAR_MAX_SIZE_BYTES
    });
    if (result) {
      setImage(result); // only update state here
    }
  };

  return (
    <>
      <AppLayout>
        <Container className='desktop-content'>
          <Group as='form' onSubmit={onSubmit}>
            {!isMobile ? (
              <Header>
                <PersonalText>Invoicing</PersonalText>
                <Button
                  title='Save'
                  variant={BUTTON_TYPE.PRIMARY}
                  buttonType='submit'
                  isLoading={loading}
                  size='small'
                />
              </Header>
            ) : (
              <ResponsiveHeader title='Invoicing' />
            )}
            <ProjectContent>
              <SubContent>
                <Autotext>
                  <SectionTitle>
                    <Title>Company information</Title>
                    <Text>
                      Information about your company, which automatically shows up on all future and existing invoices.
                    </Text>
                  </SectionTitle>
                  <UserProfile className='invoice'>
                    <input type='file' onChange={handleFileRead} ref={inputRef} hidden aria-label='Upload file' />
                    <InvoiceLogo>
                      <Image
                        imageUrl={imageSource}
                        alt={showImage ? 'invoice-logo' : 'emptyProfile'}
                        width={40}
                        height={40}
                        className={showImage ? 'profile-image' : ''}
                      />
                      <Overlay role='button' onClick={handleUploadClick}>
                        <EditIcon>
                          <SVGIcon
                            name='edit-icon'
                            width={isMobile ? '30' : '20'}
                            height={isMobile ? '30' : '20'}
                            viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                            fill='var(--text-white)'
                          />
                        </EditIcon>
                      </Overlay>
                    </InvoiceLogo>
                    <UploadText>
                      <ImageUploadText role='button' onClick={handleUploadClick}>
                        Upload company logo
                      </ImageUploadText>
                      <Text> Maximum file size allowed in 500 KB in png, jpeg, jpg format. </Text>
                    </UploadText>
                  </UserProfile>
                  <InputSection>
                    <Input
                      label='Name'
                      placeHolder='Enter company name'
                      value={invoicingDetail?.name}
                      onChange={(e) => onChangeInputField('name', e.target.value)}
                    />
                    <TextArea
                      height={isMobile ? '76px' : '58px'}
                      label='Address'
                      placeHolder='Enter company address'
                      value={invoicingDetail?.address || ''}
                      onChange={(e) => onChangeInputField('address', e.target.value)}
                    />
                  </InputSection>
                </Autotext>
                <Autotext>
                  <SectionTitle>
                    <Title>Taxes Rates</Title>
                    <Text>Set the tax rates which will be available when creating invoices.</Text>
                  </SectionTitle>
                  <FormContent>
                    {invoicingDetail?.tax?.map((item, index: number) => {
                      return (
                        <InputSide key={index}>
                          <Input
                            fieldWrapperWidth={!isMobile ? '336px' : '100%'}
                            type='text'
                            placeHolder='Name'
                            value={item?.name || ''}
                            onChange={(e) => onChangeItemsInputField(index, 'name', e.target.value)}
                          />
                          <Input
                            fieldWrapperWidth={!isMobile ? '140px' : '100%'}
                            type='text'
                            placeHolder='Tax rate'
                            value={item?.value != null ? item?.value : ''}
                            onChange={(e) => onChangeItemsInputField(index, 'value', e.target.value)}
                            trailingIconName='percentage-icon'
                          />
                          <IconButton
                            iconName='updated-delete-icon'
                            size={isMobile ? ICON_BUTTON_SIZE.EXTRA_X_LARGE : ICON_BUTTON_SIZE.EXTRA_LARGE}
                            variant={ICON_BUTTON_TYPE.TERTIARY}
                            onClick={() => onDeleteItem(index)}
                          />
                        </InputSide>
                      );
                    })}
                    {Array.isArray(invoicingDetail?.tax) && invoicingDetail.tax.length < 2 && (
                      <AddTask role='button' onClick={onAddItems}>
                        <PlusIcon>
                          <SVGIcon
                            name='plus-icon'
                            width={ICON_SIZE.LG}
                            height={ICON_SIZE.LG}
                            viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                            fill='var(--brand-primary)'
                          />
                        </PlusIcon>
                        <Text1>Add Tax Rate</Text1>
                      </AddTask>
                    )}
                  </FormContent>
                </Autotext>
                <Autotext>
                  <SectionTitle>
                    <Title>Notes</Title>
                    <Text>Add frequently used notes (eg. payment instructions).</Text>
                  </SectionTitle>
                  <TextArea
                    height='58px'
                    placeHolder='Write a notes...'
                    value={invoicingDetail?.note || ''}
                    onChange={(e) => onChangeInputField('note', e.target.value)}
                  />
                </Autotext>
                <Features>
                  <Title>Issued Invoice Due</Title>
                  <InvoiceDue>
                    {invoiceDueOptions.map((option) => (
                      <RadioButtonWrapper
                        role='button'
                        key={option.id}
                        onClick={() => onChangeInputField('dueDays', option.value)}>
                        <RadioButton
                          id={option.id}
                          name='due_days'
                          value={option.value}
                          checked={invoicingDetail?.dueDays === option.value}
                          label={option.label}
                          onChange={(value) => onChangeInputField('dueDays', value)}
                        />
                      </RadioButtonWrapper>
                    ))}
                  </InvoiceDue>
                </Features>
              </SubContent>
            </ProjectContent>
          </Group>
        </Container>
      </AppLayout>
      <BottomSubNavigation rightElement={<BottomNavigationItem iconName='correct-sign-icon' onClick={onSubmit} />} />
    </>
  );
};

export default observer(Invoicing);
